import React, { useState, useEffect } from 'react';
import { XIcon } from 'lucide-react';

interface CancelReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  reminderTitle: string;
  isLoading: boolean;
  error: string | null;
}

export function CancelReminderModal({
  isOpen,
  onClose,
  onConfirm,
  reminderTitle,
  isLoading,
  error,
}: CancelReminderModalProps) {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset reason when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setValidationError(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!reason.trim()) {
      setValidationError('Please provide a reason for cancellation');
      return;
    }

    setValidationError(null);
    onConfirm(reason);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#212121]">
            Cancel Reminder
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Provide a reason so it shows up on the timeline.
          </p>
          {(error || validationError) && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {error || validationError}
            </div>
          )}
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setValidationError(null);
            }}
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            placeholder="e.g., Client rescheduled to next week"
          />
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`flex-1 font-semibold py-3 rounded-xl ${
              isLoading
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-red-500 text-white'
            }`}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Reminder'}
          </button>
        </div>
      </div>
    </div>
  );
}
